import React, { useState } from 'react';
import {
  Zap,
  Calendar,
  Clock,
  User,
  Compass,
  ArrowRight,
  Filter,
  Shield,
  Sparkles,
  AlertCircle
} from 'lucide-react';
import { TimingRadarEvent, PracticeClient, PracticeCase } from '../../types/practiceManagement';
import { Language } from '../../types/kundali';

interface TimingRadarProps {
  events: TimingRadarEvent[];
  clients: PracticeClient[];
  cases: PracticeCase[];
  lang: Language;
  onOpenClientProfile: (client: PracticeClient) => void;
  onOpenCaseWorkspace: (caseItem: PracticeCase) => void;
  onOpenProAnalysis?: (caseItem: PracticeCase) => void;
}

export const TimingRadar: React.FC<TimingRadarProps> = ({
  events,
  clients,
  cases,
  lang,
  onOpenClientProfile,
  onOpenCaseWorkspace,
  onOpenProAnalysis
}) => {
  const isHi = lang === 'hi';
  const [horizonFilter, setHorizonFilter] = useState<'30' | '60' | '90' | 'ALL'>('ALL');
  const [confluenceFilter, setConfluenceFilter] = useState<'ALL' | 'HIGH' | 'MODERATE'>('ALL');

  const filteredEvents = events.filter((ev) => {
    const days = ev.daysUntilEvent ?? ev.daysRemaining ?? 30;
    const matchHorizon =
      horizonFilter === 'ALL' ||
      (horizonFilter === '30' && days <= 30) ||
      (horizonFilter === '60' && days <= 60) ||
      (horizonFilter === '90' && days <= 90);

    const matchConfluence =
      confluenceFilter === 'ALL' ||
      (confluenceFilter === 'HIGH' && (ev.confluenceLevel === 'HIGH' || ev.confluenceLevel === 'HIGH_CONFLUENCE')) ||
      (confluenceFilter === 'MODERATE' && (ev.confluenceLevel === 'MODERATE' || ev.confluenceLevel === 'MODERATE_DEVELOPING' || ev.confluenceLevel === 'FAVORABLE_ALIGNMENT'));

    return matchHorizon && matchConfluence;
  });

  return (
    <div className="space-y-4">
      {/* Header & Controls */}
      <div className="bg-white dark:bg-slate-850 p-4 rounded-xl border border-astro-border/90 dark:border-astro-strong-border/80 shadow-xs flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="p-2 rounded-lg bg-astro-secondary-surface0/15 text-amber-600 dark:text-amber-400">
            <Zap className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-sm font-black text-astro-text dark:text-astro-muted">
              {isHi ? 'दीर्घकालिक फल-प्रदाता गोचर रडार' : 'Longitudinal Timing & Confluence Radar'}
            </h2>
            <p className="text-xs text-astro-muted">
              {isHi
                ? 'सक्रिय क्लाइंट्स के जीवन में आगामी 30–90 दिनों के मुख्य ग्रह संमिलन'
                : 'Deterministic Dasha transitions and planetary triggers across active client base'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 text-xs">
            <span className="font-semibold text-astro-muted">{isHi ? 'अवधि:' : 'Horizon:'}</span>
            <select
              value={horizonFilter}
              onChange={(e) => setHorizonFilter(e.target.value as any)}
              className="px-2.5 py-1.5 rounded-lg bg-astro-secondary-surface dark:bg-astro-secondary text-xs font-bold border border-astro-border dark:border-astro-strong-border"
            >
              <option value="ALL">All Upcoming</option>
              <option value="30">Next 30 Days</option>
              <option value="60">Next 60 Days</option>
              <option value="90">Next 90 Days</option>
            </select>
          </div>

          <div className="flex items-center gap-1.5 text-xs">
            <span className="font-semibold text-astro-muted">{isHi ? 'संमिलन स्तर:' : 'Confluence:'}</span>
            <select
              value={confluenceFilter}
              onChange={(e) => setConfluenceFilter(e.target.value as any)}
              className="px-2.5 py-1.5 rounded-lg bg-astro-secondary-surface dark:bg-astro-secondary text-xs font-bold border border-astro-border dark:border-astro-strong-border"
            >
              <option value="ALL">All Confluences</option>
              <option value="HIGH">High Priority Only</option>
              <option value="MODERATE">Moderate</option>
            </select>
          </div>
        </div>
      </div>

      {/* Events Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
        {filteredEvents.map((ev) => {
          const client = clients.find((c) => c.id === ev.clientId);
          const relatedCase = cases.find((c) => c.clientId === ev.clientId);

          return (
            <div
              key={ev.id}
              className="bg-white dark:bg-slate-850 p-4 rounded-xl border border-astro-border/90 dark:border-astro-strong-border/80 shadow-xs hover:shadow-md transition-all hover:border-amber-400/50 flex flex-col justify-between gap-3"
            >
              <div className="space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-sm text-astro-text dark:text-astro-muted">
                        {ev.clientName}
                      </span>
                      <span className="font-mono text-xs text-astro-muted">
                        ({ev.clientRefId})
                      </span>
                    </div>
                    <div className="text-xs font-bold text-[#52635F] dark:text-amber-400 pt-0.5">
                      {ev.title || ev.eventTitle}
                    </div>
                  </div>

                  <span
                    className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                      ev.confluenceLevel === 'HIGH' || ev.confluenceLevel === 'HIGH_CONFLUENCE'
                        ? 'bg-rose-500/15 text-rose-700 dark:text-rose-300 border border-rose-500/30'
                        : 'bg-astro-secondary-surface0/15 text-astro-secondary dark:text-amber-300 border border-astro-accent/30'
                    }`}
                  >
                    {ev.confluenceLabel || `${ev.confluenceLevel} CONFLUENCE`}
                  </span>
                </div>

                <p className="text-xs text-astro-secondary-text dark:text-astro-muted bg-astro-secondary-surface dark:bg-astro-primary p-2.5 rounded-lg border border-astro-border dark:border-astro-strong-border">
                  {ev.description || ev.summary}
                </p>

                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="p-2 rounded bg-astro-secondary-surface dark:bg-astro-primary border border-astro-border dark:border-astro-strong-border">
                    <span className="text-astro-muted block text-[10px]">Dasha Factor</span>
                    <span className="font-bold text-astro-text dark:text-astro-muted truncate block">
                      {ev.dashaFactor || ev.relevantDasha || 'Dasha Trigger'}
                    </span>
                  </div>
                  <div className="p-2 rounded bg-astro-secondary-surface dark:bg-astro-primary border border-astro-border dark:border-astro-strong-border">
                    <span className="text-astro-muted block text-[10px]">Transit Factor</span>
                    <span className="font-bold text-astro-text dark:text-astro-muted truncate block">
                      {ev.transitFactor || ev.transitTrigger || 'Gochar Activation'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-astro-border dark:border-astro-strong-border">
                <div className="flex items-center gap-1.5 text-xs font-bold text-astro-secondary-text dark:text-astro-muted">
                  <Calendar className="w-3.5 h-3.5 text-amber-500" />
                  <span>
                    {ev.windowStart || ev.triggerDate} (in {ev.daysUntilEvent ?? ev.daysRemaining ?? 30} days)
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  {client && (
                    <button
                      onClick={() => onOpenClientProfile(client)}
                      className="px-2.5 py-1.5 rounded-lg bg-astro-secondary-surface dark:bg-astro-secondary hover:bg-astro-card text-astro-secondary-text dark:text-astro-muted font-bold text-xs cursor-pointer"
                    >
                      Profile
                    </button>
                  )}

                  {relatedCase && onOpenProAnalysis && (
                    <button
                      onClick={() => onOpenProAnalysis(relatedCase)}
                      className="px-2.5 py-1.5 rounded-lg bg-astro-secondary-surface0 hover:bg-astro-secondary text-white font-bold text-xs flex items-center gap-1 cursor-pointer shadow-xs"
                    >
                      <Compass className="w-3.5 h-3.5" />
                      <span>Pro Analysis</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
